import { IAnalyticsService, IMessagingService } from "../services";
import { TabInteractionEvent } from "../models/analytics";
import { AnalyticsEventName } from "../enums/analyticsEventName"
import { Component } from "./component";
import { debounce } from "../utils";

class TabsComponent extends Component {
    private tabsEl: HTMLElement;
    private tabsListOuterEl: HTMLElement;
    private tabsListEl: HTMLElement;
    private tabsListInnerEl: HTMLElement;
    private tabItems: Map<string, {
        tabTrigger: HTMLElement,
        tabPanel: HTMLElement
    }>;
    private tabsBtnForwardEl: HTMLElement;
    private tabsBtnBackEl: HTMLElement;
    private onWindowHashChange: EventListener;
    private onTabListClick: EventListener;
    private onTabListScroll: EventListener;
    private onBackForwardBtnClick: EventListener;
    private observeTabsResize: ResizeObserver;
    private hasScroll: boolean;
    private scrollTolerance: number;
    private analyticsTrackingService: IAnalyticsService;

    constructor(componentEl: HTMLElement, messagingService: IMessagingService, analyticsService: IAnalyticsService) {
        super(componentEl, messagingService);

        this.analyticsTrackingService = analyticsService;

        // bind elements
        this.tabsEl = componentEl;
        this.tabsListOuterEl = this.tabsEl.querySelector('.js-tabs-component-tabslist-outer');
        this.tabsListEl = this.tabsEl.querySelector('.js-tabs-component-tabslist');
        this.tabsListInnerEl = this.tabsEl.querySelector('.js-tabs-component-tabslist-inner');
        this.tabsBtnBackEl = this.tabsEl.querySelector('.js-tabs-btn-back');
        this.tabsBtnForwardEl = this.tabsEl.querySelector('.js-tabs-btn-forward');
        this.tabItems = new Map();

        this.tabsEl.querySelectorAll<HTMLElement>("button[aria-controls]").forEach((button) => {
            const buttonControls = button.getAttribute("aria-controls");
            if (this.tabItems.get(buttonControls)) {
                return; // Duplicate tab item ID
            }
            const buttonTarget = componentEl.querySelector<HTMLElement>(`#${buttonControls}`);
            if (!buttonTarget) {
                return;  // no tab panel found with id '${buttonControls}'
            }

            this.tabItems.set(button.id, {
                tabTrigger: button,
                tabPanel: buttonTarget
            });
        });

        if (this.tabItems.size < 1 || !this.tabsListEl || !this.tabsListInnerEl || !this.tabsBtnBackEl || !this.tabsBtnForwardEl) {
            // no items found
            if (this.tabsListOuterEl) {
                this.tabsListEl.parentElement.style.display = 'none'; // Hide tabs when component not constructed properly
            }
            return;
        }

        // Events
        this.onWindowHashChange = this.onWindowHashChangeHandler.bind(this);
        this.onTabListClick = this.onTabListClickHandler.bind(this);
        this.onTabListScroll = this.onTabListScrollHandler.bind(this);
        this.onBackForwardBtnClick = this.onBackForwardBtnClickHandler.bind(this);

        if ('ResizeObserver' in window) {
            this.observeTabsResize = new ResizeObserver(debounce(() => {
                this.calculateToggleTabMaxWidths();
                this.calculateToggleScrollVisibility();
            }, 100));
        }

        // initialise component
        this.init();
        
    }

    protected async init() {

        // set defaults
        this.hasScroll = false;
        this.toggleTab([...this.tabItems.values()][0].tabTrigger, false); // Set/Select first tab on load

        // Utility offset (in px) when scrolling back/forward to provide margin of error for calculations
        this.scrollTolerance = 15;

        // Attach event handlers
        window.addEventListener('hashchange', this.onWindowHashChange);
        
        this.tabsListEl.addEventListener('click', this.onTabListClick);
        this.tabsListEl.addEventListener('scroll', this.onTabListScroll);

        this.tabsBtnBackEl.addEventListener('click', this.onBackForwardBtnClick);
        this.tabsBtnForwardEl.addEventListener('click', this.onBackForwardBtnClick);

        this.tabItems.forEach(({ tabTrigger, tabPanel }) => {
            tabTrigger.addEventListener('focus', (e) => {
                if (!this.hasScroll) {
                    return;
                }
                const tabTrigger = e.target as HTMLElement;
                this.scrollTabIntoView(tabTrigger);
            });
            tabPanel.addEventListener('keydown', (e) => {
                // If user has selected a tab, then presses Shift-Tab key
                // We send them back to the previously selected tab
                if (e.key === "Tab" && e.shiftKey) {
                    tabTrigger.focus();
                    e.preventDefault();
                }
            });
        });

        if ('ResizeObserver' in window) {
            this.observeTabsResize.observe(this.tabsListEl);
        }

        this.calculateToggleTabMaxWidths();
        this.calculateToggleScrollVisibility();
        this.jumpToTabViaHash(window.location.hash);

    }

    private onWindowHashChangeHandler() {
        this.jumpToTabViaHash(window.location.hash);
    }

    private onTabListClickHandler(e: MouseEvent) {
        const target = e.target as HTMLElement;
        if (!target.hasAttribute("aria-controls")) {
            return;
        }
        this.toggleTab(e.target, true);
    }

    private onTabListScrollHandler() {
        this.calculateToggleScrollStates();
    }

    private onBackForwardBtnClickHandler(e) {
        // Back Button
        if (e.currentTarget === this.tabsBtnBackEl) {
            const indexOfFirstTabInView = [...this.tabItems.values()].findIndex(({ tabTrigger }) => {
                return this.getXPos(tabTrigger) + this.scrollTolerance > this.getXPos(this.tabsBtnBackEl, true);
            });
            if (indexOfFirstTabInView > 0) {
                const {tabTrigger: previousTab} = [...this.tabItems.values()][indexOfFirstTabInView - 1];
                if (previousTab) {
                    this.scrollTabIntoView(previousTab);
                }
            }
        } 
        // Forward Button
        else if (e.currentTarget === this.tabsBtnForwardEl) {
            const indexOfFirstTabOutOfView = [...this.tabItems.values()].findIndex(({ tabTrigger }) => {
                return (this.getXPos(tabTrigger, true) - this.scrollTolerance) > this.getXPos(this.tabsBtnForwardEl);
            });
            if (indexOfFirstTabOutOfView >= 0) {
                const {tabTrigger: nextTab} = [...this.tabItems.values()][indexOfFirstTabOutOfView];
                if (nextTab) {
                    this.scrollTabIntoView(nextTab);
                }
            }
        }
    }

    private getXPos(el: HTMLElement, useRightEdge: boolean = false) {
        const {x, width} = el.getBoundingClientRect();
        return useRightEdge ? x + width : x;
    }

    private toggleTab(selectedTab: EventTarget, shouldFocusToPanel: boolean) {
        this.tabItems.forEach(({ tabTrigger, tabPanel}) => {
            const targetState = tabTrigger === selectedTab;
            tabTrigger.setAttribute("aria-expanded", targetState.toString());
            tabPanel.setAttribute("aria-hidden", (!targetState).toString());
            if (targetState && shouldFocusToPanel) {
                this.analyticsTrackingService.RecordEvent(AnalyticsEventName.TabInteraction, new TabInteractionEvent(tabPanel.dataset['title']))
                tabPanel.focus();
            }
        });
    }

    private calculateToggleScrollVisibility() {
        if (this.tabsEl.offsetWidth < this.tabsListInnerEl.scrollWidth) {
            this.tabsEl.classList.add('tabs-component--has-scroll');
            this.hasScroll = true;
            this.calculateToggleScrollStates();
        } else {
            this.tabsEl.classList.remove('tabs-component--has-scroll');
            this.hasScroll = false;
        }
    }

    private calculateToggleScrollStates() {
        // Calculate is at beginning (for back button state)
        this.setToggleScrollState(this.tabsBtnBackEl, this.tabsListEl.scrollLeft < this.scrollTolerance);
        // Calculate is at end (for forward button state)
        this.setToggleScrollState(this.tabsBtnForwardEl, (this.tabsListInnerEl.scrollWidth - this.tabsListEl.scrollLeft) <= this.tabsListInnerEl.offsetWidth + this.scrollTolerance);
    }

    private calculateToggleTabMaxWidths() {
        this.tabsListEl.style.setProperty("--tab-max-width", `${this.tabsListInnerEl.offsetWidth * 0.9}px`); // Set to max 90% of Tab list width
    }

    private setToggleScrollState(btn: HTMLElement, disabled: boolean) {
        disabled ? btn.setAttribute("disabled", "disabled") : btn.removeAttribute("disabled");
        disabled ? btn.setAttribute("aria-disabled", "true") : btn.removeAttribute("aria-disabled");
    }

    private scrollTabIntoView(targetTab: HTMLElement) {
        // native element.scrollIntoView() method not well supported in Safari, so need to roll our own
        const targetTabOffsetLeftEdge = this.getXPos(this.tabsListEl);
        const targetTabLeftEdge = this.getXPos(targetTab) - targetTabOffsetLeftEdge;
        const targetTabRightEdge = this.getXPos(targetTab, true) - targetTabOffsetLeftEdge;
        const tabsListElWidth = this.tabsListEl.offsetWidth;

        if (targetTabLeftEdge < 0) {
            this.tabsListEl.scrollBy(targetTabLeftEdge, 0);
        } else if (targetTabRightEdge > tabsListElWidth) {
            this.tabsListEl.scrollBy(targetTabRightEdge - tabsListElWidth, 0);
        }
    }

    private jumpToTabViaHash(hash: string) {
        const hashValue = hash.substr(1);
        if (!hashValue) {
            return;
        }
        if (this.tabItems.get(hashValue)) {
            const {tabTrigger: tabRef} = this.tabItems.get(hashValue);
            if (tabRef) {
                this.toggleTab(tabRef, true);
                this.scrollTabIntoView(tabRef);
            }
        }
    }
    
};

export { TabsComponent };
